import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Route-level loading fallbacks that match the destination page.
 * - Public pages: correct surface (white studio vs navy) + matching geometry.
 * - Portals: correct data-portal theme + a frame that mirrors the portal shell.
 * Placeholders are decorative (aria-hidden); one translated status is announced.
 * Shimmer is static under prefers-reduced-motion (see index.css).
 */

const LOCALE_PREFIX = /^\/(en|fr|es|it|se|sv|ar|de|nl)(?=\/|$)/;
const strip = (p: string) => p.replace(LOCALE_PREFIX, "") || "/";

type Surface = "light" | "dark" | "quiet";
type Shape = "marketing" | "document" | "form" | "docs";

function resolvePublic(path: string): { surface: Surface; shape: Shape } {
  const p = strip(path);
  // Storefront theme is unknown until loaded: quiet progress line only.
  if (/^\/c\//.test(p) || /^\/[^/]+\/(q|d|track)\//.test(p) && !/^\/(q|d)\//.test(p)) {
    return { surface: "quiet", shape: "document" };
  }
  if (/^\/(q|d|s)\//.test(p)) return { surface: "dark", shape: "document" };
  if (/^\/track/.test(p)) return { surface: "dark", shape: "form" };
  if (/^\/(reset-password|forgot-password|courier\/setup|developers\/auth)/.test(p)) return { surface: "dark", shape: "form" };
  if (/^\/developers/.test(p)) return { surface: "dark", shape: "docs" };
  if (/^\/partnerships/.test(p)) return { surface: "dark", shape: "marketing" };
  if (/^\/(verify|manifest|review|pickup)\b/.test(p)) return { surface: "light", shape: "document" };
  return { surface: "light", shape: "marketing" };
}

const Status = ({ label }: { label: string }) => (
  <span className="sr-only" role="status" aria-live="polite">{label}</span>
);

export const PublicRouteLoader = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const label = t("appShell.common.loading", "Loading");
  const { surface, shape } = resolvePublic(pathname);

  if (surface === "quiet") {
    return (
      <div className="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden" aria-busy="true">
        <div className="h-full w-1/3 bg-[var(--color-orange)] ec-progress-line" aria-hidden />
        <Status label={label} />
      </div>
    );
  }

  const dark = surface === "dark";
  const bg = dark ? "bg-[var(--color-navy)]" : "bg-background";
  const b = dark ? "ec-shimmer-dark" : "ec-shimmer";

  return (
    <div className={`min-h-screen ${bg}`} aria-busy="true">
      <Status label={label} />
      <div aria-hidden className="mx-auto max-w-6xl px-4 md:px-6">
        {/* Site header stays visible above; reserve its height only */}
        <div className="h-16" />

        {shape === "marketing" && (
          <div className="pt-12 md:pt-20 space-y-16">
            <div className="grid items-center gap-5 sm:gap-10 md:grid-cols-2">
              <div className="space-y-4">
                <div className={`h-3 w-28 ${b}`} />
                <div className={`h-10 w-full ${b}`} />
                <div className={`h-10 w-4/5 ${b}`} />
                <div className={`h-4 w-3/4 ${b}`} />
                <div className="flex gap-3 pt-4">
                  <div className={`h-11 w-36 ${b}`} />
                  <div className={`h-11 w-36 ${b}`} />
                </div>
              </div>
              <div className={`aspect-[4/3] w-full ${b}`} />
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3">
                  <div className={`h-12 w-12 ${b}`} />
                  <div className={`h-4 w-2/3 ${b}`} />
                  <div className={`h-3 w-full ${b}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {shape === "document" && (
          <div className="mx-auto max-w-2xl space-y-6 pt-12">
            <div className={`h-8 w-2/3 ${b}`} />
            <div className={`h-4 w-1/2 ${b}`} />
            <div className="space-y-3 pt-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex justify-between gap-4">
                  <div className={`h-4 w-1/3 ${b}`} />
                  <div className={`h-4 w-1/4 ${b}`} />
                </div>
              ))}
            </div>
            <div className={`h-12 w-full ${b}`} />
          </div>
        )}

        {shape === "form" && (
          <div className="mx-auto max-w-md space-y-5 pt-12 md:pt-20">
            <div className={`mx-auto h-8 w-2/3 ${b}`} />
            <div className={`mx-auto h-4 w-1/2 ${b}`} />
            <div className={`h-12 w-full ${b}`} />
            <div className={`h-12 w-full ${b}`} />
          </div>
        )}

        {shape === "docs" && (
          <div className="grid gap-4 sm:gap-8 pt-10 md:grid-cols-[220px_1fr]">
            <div className="hidden space-y-3 md:block">
              {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`h-4 w-3/4 ${b}`} />)}
            </div>
            <div className="space-y-4">
              <div className={`h-9 w-1/2 ${b}`} />
              <div className={`h-4 w-full ${b}`} />
              <div className={`h-4 w-5/6 ${b}`} />
              <div className={`h-40 w-full ${b}`} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type PortalId = "admin" | "support" | "customer" | "crm" | "affiliate" | "partner" | "courier";

function resolvePortal(path: string): { id: PortalId; shape: "dashboard" | "list" } {
  const p = strip(path);
  const id: PortalId =
    p.startsWith("/admin") ? "admin" :
    p.startsWith("/support-portal") ? "support" :
    p.startsWith("/account") ? "customer" :
    p.startsWith("/crm") ? "crm" :
    p.startsWith("/affiliate") ? "affiliate" :
    p.startsWith("/partner") ? "partner" : "courier";
  const segs = p.split("/").filter(Boolean);
  return { id, shape: segs.length <= 1 ? "dashboard" : "list" };
}

export const PortalRouteLoader = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const label = t("appShell.common.loading", "Loading");
  const { id, shape } = resolvePortal(pathname);

  return (
    <div data-portal={id} className="flex min-h-screen bg-[var(--portal-bg)]" aria-busy="true">
      <Status label={label} />
      {/* Sidebar frame keeps logo/menu position stable on desktop */}
      <aside aria-hidden className="hidden w-60 shrink-0 space-y-3 p-4 lg:block">
        <div className="mb-6 h-8 w-32 ec-skel" />
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-9 w-full ec-skel" />)}
      </aside>
      <main aria-hidden className="flex-1 p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-7 w-48 ec-skel" />
            <div className="h-9 w-28 ec-skel" />
          </div>
          {shape === "dashboard" ? (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 ec-skel" />)}
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                <div className="h-72 ec-skel lg:col-span-2" />
                <div className="h-72 ec-skel" />
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="h-10 flex-1 ec-skel" />
                <div className="h-10 w-32 ec-skel" />
              </div>
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-14 w-full ec-skel" />)}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
